<template>
  <div
    id="Benefits"
    class="relative bg-white"
  >
    <div class="mx-auto max-w-3xl px-8 py-10 md:py-10 lg:max-w-6xl lg:py-14">
      <div class="lg:flex lg:gap-10">
        <div class="relative w-full md:relative md:flex lg:w-1/2">
          <div
            class="-bottom-0 -left-0 z-10 hidden md:relative md:block lg:absolute lg:block"
          >
            <NuxtImg
              src="/website/scope-card-02.svg"
              class="hidden md:block md:w-60 lg:block lg:w-60"
              alt="Scope SVG"
            />
          </div>
          <div
            class="feature-cirle mx-auto w-48 overflow-hidden rounded-full md:mx-auto md:h-60 md:w-60 lg:h-96 lg:w-96 lg:bg-gray-200"
          >
            <NuxtImg
              src="/website/circle.png"
              class="relative object-cover"
              alt="People planning tasks on the board inside the office"
            />
          </div>
          <div
            class="-right-0 top-0 z-10 hidden md:relative md:block lg:absolute lg:block"
          >
            <NuxtImg
              src="/website/scope-card-01.svg"
              class="hovering hidden md:block md:w-60 lg:block lg:w-64"
              alt="Scope SVG"
            />
          </div>
          <div
            class="bottom-0 right-20 z-10 hidden md:hidden lg:absolute lg:block"
          >
            <NuxtImg
              src="/website/scribble-05.svg"
              class="hidden md:block md:w-24 lg:block lg:w-24"
              alt="Sribble"
            />
          </div>
        </div>

        <div class="relative my-auto mt-10 w-full lg:w-1/2">
          <div>
            <div class="relative max-w-lg lg:max-w-xl">
              <h3
                class="mx-auto mb-4 px-0 font-sans text-2xl font-semibold text-[#20484F] antialiased lg:mb-0 lg:text-3xl"
              >
                Your Roadmap to <span class="span-underline">Net Zero</span>
              </h3>
            </div>
            <div class="mb-8 max-w-lg md:mb-10 lg:mb-14 lg:max-w-lg">
              <p class="mt-6 text-base text-gray-500 md:text-base lg:text-base">
                Veridis is your one-stop carbon management platform that allows
                you to monitor emissions, generate sustainability reports,
                perform scenario analysis and track real-time progress to reduce
                your carbon footprint.
              </p>
            </div>
            <div class="flex gap-3 md:space-x-3">
              <NuxtLink
                to="/contact-us"
                class="block w-full select-none rounded-md border-2 border-green-800/50 bg-green-700 px-5 py-3 text-center text-sm font-normal text-white hover:bg-green-800 active:bg-green-700 md:w-40 lg:w-auto lg:px-10 lg:py-4 lg:text-sm"
              >
                <span>Talk to Us</span>
              </NuxtLink>
            </div>
          </div>
        </div>
      </div>

      <div class="mt-14 grid grid-cols-2 lg:grid-cols-4 lg:gap-0">
        <div
          class="relative overflow-hidden border border-r-0 border-gray-200 bg-white px-3 py-3 lg:mb-0 lg:rounded-l-full lg:px-10 lg:py-10"
        >
          <div
            class="card-icon mb-5 flex h-9 w-9 items-center justify-center rounded-full border border-green-200 bg-green-50"
          >
            <span>1</span>
          </div>
          <span
            class="block text-base font-medium leading-tight text-gray-900 lg:text-base lg:leading-tight"
          >
            Choose Scope
          </span>
          <div
            class="absolute right-0 top-0 h-full w-5 md:block"
            aria-hidden="true"
          >
            <svg
              class="h-full w-full text-gray-300"
              viewBox="0 0 12 82"
              fill="none"
              preserveAspectRatio="none"
            >
              <path
                d="M0.5 0V31L10.5 41L0.5 51V82"
                stroke="currentcolor"
                vector-effect="non-scaling-stroke"
              />
            </svg>
          </div>
        </div>

        <div
          class="relative overflow-hidden border border-l-0 border-r-0 border-gray-200 bg-white px-3 py-3 lg:mb-0 lg:px-10 lg:py-10"
        >
          <div
            class="card-icon mb-5 flex h-9 w-9 items-center justify-center rounded-full border border-green-200 bg-green-50"
          >
            <span>2</span>
          </div>
          <span
            class="block text-base font-medium leading-tight text-gray-900 lg:text-base lg:leading-tight"
          >
            Pick your Emission Category and Type
          </span>
          <div
            class="absolute right-0 top-0 h-full w-5 md:block"
            aria-hidden="true"
          >
            <svg
              class="h-full w-full text-gray-300"
              viewBox="0 0 12 82"
              fill="none"
              preserveAspectRatio="none"
            >
              <path
                d="M0.5 0V31L10.5 41L0.5 51V82"
                stroke="currentcolor"
                vector-effect="non-scaling-stroke"
              />
            </svg>
          </div>
        </div>

        <div
          class="relative border border-l-0 border-r-0 border-gray-200 bg-white px-3 py-3 lg:mb-0 lg:px-10 lg:py-10"
        >
          <div
            class="card-icon mb-5 flex h-9 w-9 items-center justify-center rounded-full border border-green-200 bg-green-50"
          >
            <span>3</span>
          </div>
          <span
            class="block text-base font-medium leading-tight text-gray-900 lg:text-base lg:leading-tight"
          >
            Add Activity Data
          </span>
          <div
            class="absolute right-0 top-0 h-full w-5 md:block"
            aria-hidden="true"
          >
            <svg
              class="h-full w-full text-gray-300"
              viewBox="0 0 12 82"
              fill="none"
              preserveAspectRatio="none"
            >
              <path
                d="M0.5 0V31L10.5 41L0.5 51V82"
                stroke="currentcolor"
                vector-effect="non-scaling-stroke"
              />
            </svg>
          </div>
        </div>

        <div
          class="relative border border-l-0 border-r-0 border-gray-200 bg-white px-3 py-3 lg:mb-0 lg:px-10 lg:py-10"
        >
          <div
            class="card-icon mb-5 flex h-9 w-9 items-center justify-center rounded-full border border-green-200 bg-green-50"
          >
            <span>4</span>
          </div>
          <span
            class="block text-base font-medium leading-tight text-gray-900 lg:text-base lg:leading-tight"
          >
            Choose Calculation Method
          </span>
          <div
            class="absolute right-0 top-0 h-full w-5 md:block"
            aria-hidden="true"
          >
            <svg
              class="h-full w-full text-gray-300"
              viewBox="0 0 12 82"
              fill="none"
              preserveAspectRatio="none"
            >
              <path
                d="M0.5 0V31L10.5 41L0.5 51V82"
                stroke="currentcolor"
                vector-effect="non-scaling-stroke"
              />
            </svg>
          </div>
        </div>

        <div
          class="relative overflow-hidden border border-l-0 border-r-0 border-t-0 border-gray-200 bg-white px-3 py-3 md:rounded-l-full lg:mb-0 lg:rounded-l-full lg:border-l lg:px-10 lg:py-10"
        >
          <div
            class="card-icon mb-5 flex h-9 w-9 items-center justify-center rounded-full border border-green-200 bg-green-50"
          >
            <span>5</span>
          </div>
          <span
            class="block text-base font-medium leading-tight text-gray-900 lg:text-base lg:leading-tight"
          >
            Calculate Emissions
          </span>
          <div
            class="absolute right-0 top-0 h-full w-5 md:block"
            aria-hidden="true"
          >
            <svg
              class="h-full w-full text-gray-300"
              viewBox="0 0 12 82"
              fill="none"
              preserveAspectRatio="none"
            >
              <path
                d="M0.5 0V31L10.5 41L0.5 51V82"
                stroke="currentcolor"
                vector-effect="non-scaling-stroke"
              />
            </svg>
          </div>
        </div>

        <div
          class="relative border border-l-0 border-r-0 border-t-0 border-gray-200 bg-white px-3 py-3 lg:mb-0 lg:px-10 lg:py-10"
        >
          <div
            class="card-icon mb-5 flex h-9 w-9 items-center justify-center rounded-full border border-green-200 bg-green-50"
          >
            <span>6</span>
          </div>
          <span
            class="block text-base font-medium leading-tight text-gray-900 lg:text-base lg:leading-tight"
          >
            View Results Breakdown
          </span>
          <div
            class="absolute right-0 top-0 h-full w-5 md:block"
            aria-hidden="true"
          >
            <svg
              class="h-full w-full text-gray-300"
              viewBox="0 0 12 82"
              fill="none"
              preserveAspectRatio="none"
            >
              <path
                d="M0.5 0V31L10.5 41L0.5 51V82"
                stroke="currentcolor"
                vector-effect="non-scaling-stroke"
              />
            </svg>
          </div>
        </div>

        <div
          class="relative border border-l-0 border-r-0 border-t-0 border-gray-200 bg-white px-3 py-3 lg:mb-0 lg:px-10 lg:py-10"
        >
          <div
            class="card-icon mb-5 flex h-9 w-9 items-center justify-center rounded-full border border-green-200 bg-green-50"
          >
            <span>7</span>
          </div>
          <span
            class="block text-base font-medium leading-tight text-gray-900 lg:text-base lg:leading-tight"
          >
            Generate Sustainability Reports
          </span>
          <div
            class="absolute right-0 top-0 h-full w-5 md:block"
            aria-hidden="true"
          >
            <svg
              class="h-full w-full text-gray-300"
              viewBox="0 0 12 82"
              fill="none"
              preserveAspectRatio="none"
            >
              <path
                d="M0.5 0V31L10.5 41L0.5 51V82"
                stroke="currentcolor"
                vector-effect="non-scaling-stroke"
              />
            </svg>
          </div>
        </div>

        <div
          class="relative border border-l-0 border-r-0 border-t-0 border-gray-200 bg-white px-3 py-3 lg:mb-0 lg:px-10 lg:py-10"
        >
          <div
            class="card-icon mb-5 flex h-9 w-9 items-center justify-center rounded-full border border-green-200 bg-green-50"
          >
            <span>8</span>
          </div>
          <span
            class="block text-base font-medium leading-tight text-gray-900 lg:text-base lg:leading-tight"
          >
            Receive Emissions Reduction Recommendations (coming soon)
          </span>
          <div
            class="absolute right-0 top-0 h-full w-5 md:block"
            aria-hidden="true"
          >
            <svg
              class="h-full w-full text-gray-300"
              viewBox="0 0 12 82"
              fill="none"
              preserveAspectRatio="none"
            >
              <path
                d="M0.5 0V31L10.5 41L0.5 51V82"
                stroke="currentcolor"
                vector-effect="non-scaling-stroke"
              />
            </svg>
          </div>
        </div>

        <div
          class="relative overflow-hidden border border-l-0 border-r-0 border-t-0 border-gray-200 bg-white px-3 py-3 md:rounded-l-full lg:mb-0 lg:rounded-l-full lg:border-l lg:px-10 lg:py-10"
        >
          <div
            class="card-icon mb-5 flex h-9 w-9 items-center justify-center rounded-full border border-green-200 bg-green-50"
          >
            <span>9</span>
          </div>
          <span
            class="block text-base font-medium leading-tight text-gray-900 lg:text-base lg:leading-tight"
          >
            Offset Emissions (coming soon)
          </span>
          <div
            class="absolute right-0 top-0 h-full w-5 md:block"
            aria-hidden="true"
          >
            <svg
              class="h-full w-full text-gray-300"
              viewBox="0 0 12 82"
              fill="none"
              preserveAspectRatio="none"
            >
              <path
                d="M0.5 0V31L10.5 41L0.5 51V82"
                stroke="currentcolor"
                vector-effect="non-scaling-stroke"
              />
            </svg>
          </div>
        </div>

        <div
          class="relative border border-l-0 border-r-0 border-t-0 border-gray-200 bg-white px-3 py-3 lg:mb-0 lg:px-10 lg:py-10"
        >
          <div
            class="card-icon mb-5 flex h-9 w-9 items-center justify-center rounded-full border border-green-200 bg-green-50"
          >
            <span>10</span>
          </div>
          <span
            class="block text-base font-medium leading-tight text-gray-900 lg:text-base lg:leading-tight"
          >
            Track decarbonization progress
          </span>
          <div
            class="absolute right-0 top-0 h-full w-5 md:block"
            aria-hidden="true"
          >
            <svg
              class="h-full w-full text-gray-300"
              viewBox="0 0 12 82"
              fill="none"
              preserveAspectRatio="none"
            >
              <path
                d="M0.5 0V31L10.5 41L0.5 51V82"
                stroke="currentcolor"
                vector-effect="non-scaling-stroke"
              />
            </svg>
          </div>
        </div>
        <div
          class="relative border-0 bg-white px-3 py-3 lg:mb-0 lg:px-10 lg:py-10"
        >
          <NuxtImg
            src="/website/flag.svg"
            alt="flag"
            class="h-32 w-32"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'RoadmapSection',
}
</script>

<style>
.hovering {
  -webkit-animation-name: hovering;
  -webkit-animation-duration: 6s;
  -webkit-animation-iteration-count: infinite;
  -webkit-animation-timing-function: ease-in-out;
  -moz-animation-name: hovering;
  -moz-animation-duration: 6s;
  -moz-animation-iteration-count: infinite;
  -moz-animation-timing-function: ease-in-out;
  animation-name: hovering;
  animation-duration: 6s;
  animation-iteration-count: infinite;
  animation-timing-function: ease-in-out;
}

@-webkit-keyframes hovering {
  from {
    -webkit-transform: translate(0, 0px);
  }

  50% {
    -webkit-transform: translate(0, -15px);
  }

  to {
    -webkit-transform: translate(0, -0px);
  }
}

@-moz-keyframes hovering {
  from {
    -moz-transform: translate(0, 0px);
  }

  50% {
    -moz-transform: translate(0, -15px);
  }

  to {
    -moz-transform: translate(0, -0px);
  }
}

@keyframes hovering {
  from {
    transform: translate(0, 0px);
  }

  50% {
    transform: translate(0, -15px);
  }

  to {
    transform: translate(0, -0px);
  }
}
</style>
